import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, map, catchError, throwError } from 'rxjs';
import { saveAs } from 'file-saver';
import { SKIP_ERROR_HANDLER_HEADER } from '@core/interceptor/error-handler.interceptor';

export type ReportFormat = 'pdf' | 'excel' | 'word';

export const DEFAULT_REPORT_FORMAT: ReportFormat = 'pdf';

export interface QueryParamDTO {
  editionCode?: string;
  [key: string]: any;
}

export interface LegacyReportResponse {
  reportFile: string;
  reportFormat: ReportFormat;
}

@Injectable({ providedIn: 'root' })
export class ReportService {
  protected resourceUrl = `${environment.SERVER_READ_API_URL}/api/read`;

  private readonly isDocx = signal<boolean>(false);

  constructor(protected http: HttpClient) {}

  report(
    reportName: string,
    editionCode: string,
    reportParams: QueryParamDTO,
    format: ReportFormat = DEFAULT_REPORT_FORMAT,
    pageHeight?: number,
    pageWidth?: number,
    whereParam?: boolean
  ): Observable<LegacyReportResponse> {
    return this.http.post<LegacyReportResponse>(`${this.resourceUrl}/report/${reportName}`, {
      editionCode,
      format,
      options: reportParams?.options,
      matriceOptions: reportParams?.matriceOptions,
      pageHeight,
      pageWidth,
      whereParam,
    });
  }


  /**
   * Generate report with dynamic X-Is-Docx header (single endpoint)
   */
  generateReport(
    queryName: string,
    queryParam: QueryParamDTO
  ): Observable<Blob> {
    // Single endpoint URL - no format in path
    const url = `${this.resourceUrl}/generate/${queryName}`;

    // Set headers dynamically based on isDocx signal
    const headers = new HttpHeaders({
      'X-Is-Docx': this.isDocx().toString(),
    });

    return this.http
      .post(url, queryParam, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          // Handle the file download
          const blob = response.body;


          const fileName = response.headers.get(
            'Filename'
          );
          saveAs(blob, fileName);

          return blob;
        })
      );
  }


  /**
   * Preview report in a new browser tab (no file-save dialog)
   */
  previewReport(
    queryName: string,
    queryParam: QueryParamDTO
  ): Observable<Blob> {
    const url = `${this.resourceUrl}/generate/${queryName}`;
    const headers = new HttpHeaders({ 'X-Is-Docx': 'false' });

    return this.http
      .post(url, queryParam, { headers, responseType: 'blob', observe: 'response' })
      .pipe(
        map((response) => {
          const blob = response.body;
          const fileName = response.headers.get('Filename') ?? `${queryName}.pdf`;
          // Blob URLs opened via window.open don't carry a filename to the browser's
          // native PDF viewer, so it always falls back to "download.pdf". An anchor
          // with the download attribute is the only reliable way to name the file.
          const objectUrl = URL.createObjectURL(blob!);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = fileName;
          link.target = '_blank';
          link.click();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
          return blob;
        })
      );
  }

  /**
   * Set whether to generate DOCX format
   */
  setIsDocx(isDocx: boolean): void {
    this.isDocx.set(isDocx);
  }

  /**
   * Get current isDocx state
   */
  getIsDocx(): boolean {
    return this.isDocx();
  }


  /**
   * Generate Excel report
   */
  generateExcelReport(
    queryName: string,
    queryParam: QueryParamDTO
  ): Observable<Blob> {
    const url = `${this.resourceUrl}/excel/${queryName}`;

    const headers = new HttpHeaders({
      [SKIP_ERROR_HANDLER_HEADER]: 'true',
    });

    return this.http
      .post(url, queryParam, {
        headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const blob = response.body;
          const fileName = response.headers.get('Filename');
          saveAs(blob, fileName);
          return blob;
        }),
        catchError((err: HttpErrorResponse) => throwError(() => err))
      );
  }
}
